<template>
  <div>
    <!--EDIT ROLE MODAL -->
    <Modal
      width="431"
      v-model="editRoleModalDisplayed"
      :title="$t('settings.workplace_users_edit_modal_title')"
      :cta-text="$t('settings.workplace_users_edit_modal_cta_text')"
      :cta-loading="editRoleLoading"
      :cta-cy-text="'edit-role-confirm-btn'"
      actions-border
      @confirm="editRole"
      @cancel="resetEditRoleData">
      <div class="edit-role__content-wrapper">
        <va-text-input
          data-cy="edit-role-name-input"
          class="spacing-bottom--16"
          :value="editRoleData.name"
          disabled
          :label="$t('settings.workplace_users_edit_role_name')"></va-text-input>
        <va-select-input
          :value="editRoleData.organizationPermissions[0]"
          data-cy="edit-role-role-input"
          :label="$t('settings.workplace_users_edit_role_select')"
          :placeholder="$t('settings.workplace_users_edit_role_select_placeholder')"
          :choices="organizationRoles"
          :item-text="'title'"
          item-value="@id"
          :prepend-inner-icon="'mdi-pencil-outline'"
          :hide-details="'auto'"
          return-object
          @change="updateEditedRolePermissions">
        </va-select-input>
        <p v-if="this.selectedRole.disclaimer" class="edit-role__role-disclaimer spacing-bottom--16">
          {{ this.selectedRole.disclaimer }}
        </p>
        <va-autocomplete-input
          ref="domainsInput"
          class="mt-4"
          data-cy="edit-role-domains-input"
          v-model="editRoleData.domains"
          :label="$t('settings.workplace_users_edit_role_domains')"
          :placeholder="$t('settings.workplace_users_edit_role_domains_placeholder')"
          :choices="domainsList"
          :item-text="'name'"
          :item-value="'@id'"
          :hide-details="'auto'"
          cache-items
          @change="selectedDomainsHandler"
          @blur="handleDomainsBlur"
          multiple
          hide-select-all>
        </va-autocomplete-input>
      </div>
    </Modal>

    <!-- TRANSFER OWNERSHIP MODAL -->
    <Modal
      width="431"
      v-model="transferOwnershipModalDisplayed"
      :title="$t('settings.workplace_users_transfer_ownership_modal_title')"
      :subtitle="$t('settings.workplace_users_transfer_ownership_modal_subtitle')"
      :cta-text="$t('settings.workplace_users_transfer_ownership_modal_cta_text')"
      :cta-disabled="transferOwnershipDisabled"
      :cta-loading="transferOwnershipLoading"
      :cta-cy-text="'transfer-ownership-confirm-btn'"
      actions-border
      @confirm="transferOwnership"
      @cancel="resetTransferOwnershipData">
      <div class="transfer-ownership__content-wrapper">
        <va-select-input
          data-cy="transfer-ownership-user-input"
          v-model="transferOwnershipData.user"
          :label="$t('settings.workplace_users_transfer_ownership_user')"
          :placeholder="$t('settings.workplace_users_transfer_ownership_user_placeholder')"
          :choices="organizationUsers"
          :item-text="'email'"
          :item-value="'id'"
          :hide-details="'auto'">
        </va-select-input>
      </div>
    </Modal>

    <v-toolbar flat>
      <div>
        <v-text-field
          class="search-filter"
          hide-details
          v-model="tableSearch"
          label=""
          :placeholder="$t('generic.search_label')">
        </v-text-field>
      </div>
      <div class="spacer"></div>
      <div class="bulk-buttons" v-if="tableSelectedItems.length !== 0">
        <v-btn
          @click="editRoleAction(tableSelectedItems[0])"
          v-if="tableSelectedItems.length < 2 && tableSelectedItems[0].confirmed"
          text
          color="primary">
          <v-icon small>mdi-pencil-outline</v-icon>
          <span class="ml-2">{{ $t("generic.buttons.edit") }}</span>
        </v-btn>
        <v-btn
          @click="deleteUserAction(tableSelectedItems[0])"
          v-if="tableSelectedItems.length < 2"
          text
          color="primary">
          <v-icon small>mdi-trash-can-outline</v-icon>
          <span class="ml-2">{{ $t("generic.buttons.delete") }}</span>
        </v-btn>
        <v-btn
          @click="deleteManyUserAction(tableSelectedItems)"
          v-if="tableSelectedItems.length > 1"
          text
          color="primary">
          <v-icon small>mdi-trash-can-outline</v-icon>
          <span class="ml-2">{{ $t("generic.buttons.delete") }} ({{ tableSelectedItems.length }})</span>
        </v-btn>
      </div>
    </v-toolbar>
    <VaClientSideList
      class="va-table--selectable"
      v-model="tableSelectedItems"
      :headers="headersUsers"
      :items="tableItems"
      :search="tableSearch"
      :show-select="
        !userIsEditor(
          store.state.currentlyActiveOrganization
            ? store.state.currentlyActiveOrganization.organizationPermissions
            : undefined,
        )
      "
      :checkbox-color="'primary'"
      :selectable-key="'selectable'"
      :loading="listLoading"
      :footer-props="{
        showFirstLastPage: false,
        showCurrentPage: true,
        itemsPerPageText: 'Items per page:',
      }">
      <template #[`header.data-table-select`]="{ props, on }">
        <!-- Here we need this checkbox template to override the color (primary)       -->
        <v-checkbox
          :color="props.color"
          :indeterminate="props.indeterminate"
          :value="props.value"
          @change="on.input"></v-checkbox>
      </template>
      <!-- name -->
      <template #[`item.fullName`]="{ item }">
        <div class="d-flex align-center">
          <template v-if="item.confirmed">
            <div class="cell-ellipsis">
              <div class="ellipsis-wrapper">
                <span class="ellipsis-content text--primary">{{ item.fullName }}</span>
                <div v-if="replaceId(user.id, '/api/users/') === item.id" class="info__badge">
                  {{ $t("generic.you") }}
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="cell-ellipsis">
              <span>
                <i>{{ $t("resources.organizations.user_has_not_registered") }}</i>
              </span>
            </div>
          </template>
        </div>
      </template>
      <template #[`item.email`]="{ item }">
        <div class="cell-ellipsis">
          <span>{{ item.email }}</span>
        </div>
      </template>

      <template #[`item.roleName`]="{ item }">
        <v-chip class="mr-2 capitalize">
          {{ item.roleName }}
        </v-chip>
      </template>
      <template #item.actions="{ item }">
        <template v-if="displayTransferOwnershipBtn(item)">
          <v-btn
            data-cy="transfer-ownership-btn"
            @click="transferOwnershipModalDisplayed = true"
            small
            outlined
            color="subtitle">
            {{ $t("settings.workplace_users_transfer_ownership") }}
          </v-btn>
        </template>

        <template
          v-if="
            !item.confirmed &&
            !userIsEditor(
              store.state.currentlyActiveOrganization
                ? store.state.currentlyActiveOrganization.organizationPermissions
                : undefined,
            )
          ">
          <v-btn data-cy="cancel-invitation-btn" @click="cancelInvitation(item)" small outlined color="subtitle">
            <div class="d-flex align-center">
              <v-icon class="mr-1" color="icon" size="15">mdi-close</v-icon>
              {{ $t("settings.workplace_users_cancel_invitation") }}
            </div>
          </v-btn>
        </template>
        <!---Last condition is if user is currently logged in user -->
        <template v-if="displayEditRoleBtn(item)">
          <v-btn data-cy="edit-role-btn" class="mr-2" @click="editRoleAction(item)" small outlined color="subtitle">
            {{ $t("settings.workplace_users_edit_role") }}
          </v-btn>
          <DataListActions
            v-if="
              !userIsEditor(
                store.state.currentlyActiveOrganization
                  ? store.state.currentlyActiveOrganization.organizationPermissions
                  : undefined,
              ) && replaceId(user.id, '/api/users/') !== item.id
            "
            :actions="[
              {
                title: $t('settings.workplace_users_delete_user'),
                icon: 'mdi-trash-can-outline',
                function: deleteUserAction.bind(this, item),
              },
            ]">
          </DataListActions>
        </template>
      </template>
    </VaClientSideList>
  </div>
</template>

<script>
  // Vuex
  import store from "@/store";
  import { mapGetters, mapActions } from "vuex";
  // ACL
  import definedAbility from "@/ability";
  // Helpers
  import { replaceId } from "@/utility/replaceIdField";
  import { getRoleInfo, getMainRole, userIsEditor, userIsOwner } from "@/assets/utilities/helpers/roles";
  import cloneDeep from "lodash/cloneDeep";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  // Components
  import DataListActions from "@/components/custom/DataListActions";
  import Modal from "@/components/custom/Modal";
  import VaClientSideList from "@/components/lists/VaClientSideList";
  // Constants
  import { ROLE_OWNER } from "@/constant";

  export default {
    name: "OrganizationUsersList",
    components: {
      DataListActions,
      Modal,
      VaClientSideList,
    },
    props: {
      organizationItem: {
        type: Object,
        default: () => {},
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        replaceId,
        userIsEditor,
        userIsOwner,
        tableSelectedItems: [],
        tableSearch: "",
        store,
        ability: definedAbility(),
        headersUsers: [
          {
            text: this.$t("resources.organizations.fields.name").toUpperCase(),
            value: "fullName",
            sortable: true,
            width: "50%",
          },
          {
            text: this.$t("resources.organizations.fields.email").toUpperCase(),
            value: "email",
            sortable: true,
            width: "50%",
          },
          {
            text: this.$t("resources.organizations.fields.roles").toUpperCase(),
            value: "roleName",
            sortable: true,
            width: "184px",
          },
          {
            align: "end",
            text: this.$t("resources.organizations.fields.action").toUpperCase(),
            value: "actions",
            sortable: false,
            width: "184px",
          },
        ],
        organizationDomains: [],
        // General list loading property
        listLoadingLocalState: false,
        // Edit role properties
        organizationRoles: [],
        selectedRole: {},
        editRoleModalDisplayed: false,
        editRoleLoading: false,
        editRoleData: {
          userOrganizationId: "",
          name: "",
          role: "",
          domains: [],
          organizationPermissions: [],
          allDomainsAccess: false,
        },
        // Transfer ownership properties
        organizationUsers: [],
        transferOwnershipModalDisplayed: false,
        transferOwnershipLoading: false,
        transferOwnershipData: {
          user: "",
        },
      };
    },
    computed: {
      ...mapGetters({
        user: "getCurrentUser",
      }),
      listLoading() {
        return this.loading || this.listLoadingLocalState;
      },
      transferOwnershipDisabled() {
        return !this.transferOwnershipData.user;
      },
      tableItems() {
        if (this.organizationItem && Object.keys(this.organizationItem).length) {
          const userTableItems = this.organizationItem?.users.map(item => {
            return {
              organizationId: item.id,
              id: item.user.id,
              fullName: item.user.firstName + " " + item.user.lastName,
              email: item.user.email,
              // Convert from array of organizationPermissions to a plain String role name so that it can be sorted in the table
              roleName: item.confirmed
                ? getRoleInfo(item.organizationPermissions[0]).title_shorthand
                : this.$t("resources.organizations.pending_invitation"),
              organizationPermissions: item.organizationPermissions,
              confirmed: item.confirmed,
              domains: item.domains,
              selectable:
                !this.userIsOwner(item.organizationPermissions) &&
                replaceId(this.user.id, "/api/users/") !== item.user.id,
              allDomainsAccess: item.allDomainsAccess,
            };
          });

          return userTableItems;
        } else {
          return [];
        }
      },
      domainsList() {
        // If all connected domains is selected, disable all other options
        if (this.editRoleData.allDomainsAccess) {
          return this.organizationDomains.map(domain => {
            if (domain["@id"] !== "all_domains") {
              return {
                ...domain,
                disabled: true,
              };
            }

            return domain;
          });
        } else {
          return this.organizationDomains.map(domain => {
            return {
              ...domain,
              disabled: false,
            };
          });
        }
      },
    },
    watch: {
      domainsList() {
        /**
         * Override cached items in autocomplete component.
         * This is needed in order for autocomplete items to be disabled / enabled properly.
         */
        if (this.$refs?.domainsInput?.$refs?.autocomplete) {
          this.$refs.domainsInput.$refs.autocomplete.cachedItems = this.domainsList;
        }
      },
      /**
       * User might change the currently active organization
       */
      organizationItem: {
        async handler(newOrganization) {
          this.organizationDomains = [
            { name: "All connected domains", "@id": "all_domains" },
            ...(newOrganization?.domains ?? []),
          ];

          // All organization users except OWNER for the Transfer ownership users select.
          this.organizationUsers = newOrganization?.users
            .filter(userOrganization => {
              return !this.userIsOwner(userOrganization.organizationPermissions);
            })
            .map(userOrganization => userOrganization.user);

          // Roles for the Role/Permission select options
          this.organizationRoles = newOrganization?.organizationPermissions
            .map(organizationPermission => {
              return {
                ...organizationPermission,
                ...getRoleInfo(organizationPermission),
              };
            })
            // Remove Owner role from selection.
            .filter(organizationPermission => organizationPermission.value !== ROLE_OWNER.value);
        },
      },
    },
    methods: {
      ...mapActions({
        refreshCurrentlyActiveOrganizationData: "refreshCurrentlyActiveOrganizationData",
      }),
      handleDomainsBlur() {
        /**
         * There's a minor issue with vuetify's autocomplete -
         * Whenever user inputs something to search field, selects an item and clicks away,
         * input placeholder is not visible. That's because internally, autocomplete keeps search value and doesn't clear it.
         */
        setTimeout(() => {
          this.$refs.domainsInput.search = "";
        }, 100);
      },
      selectedDomainsHandler(selectedDomains) {
        this.editRoleData.allDomainsAccess = selectedDomains.includes("all_domains");
        // Unselect any other domains and only keep "all connected domains" selected
        if (this.editRoleData.allDomainsAccess) {
          this.editRoleData.domains = ["all_domains"];
        }
      },
      async transferOwnership() {
        this.transferOwnershipLoading = true;
        // We have to display which email address received the ownership transfer email, so we need to find the user;
        const selectedUser = this.organizationItem?.users.find(
          userOrganization => userOrganization.user.id === this.transferOwnershipData.user,
        ).user;

        try {
          await this.$admin.dataProvider.customHttpCall(
            "POST",
            `organizations/${this.organizationItem.id}/change_ownership/${this.transferOwnershipData.user}`,
          );

          this.$admin.toast.info(
            this.$t("settings.workplace_users_transfer_ownership_info", { email: selectedUser?.email }),
          );
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.transferOwnershipLoading = false;
          this.transferOwnershipModalDisplayed = false;
          this.resetTransferOwnershipData();
        }
      },
      resetTransferOwnershipData() {
        this.transferOwnershipData.user = "";
      },
      async editRole() {
        this.listLoadingLocalState = true;
        this.editRoleLoading = true;

        const editRoleData = cloneDeep(this.editRoleData);
        // Empty out the array so that "all_domains" value is not sent
        if (editRoleData.allDomainsAccess) {
          editRoleData.domains = [];
        }

        try {
          await this.$admin.dataProvider.customHttpCall(
            "PATCH",
            `user_organizations/${editRoleData.userOrganizationId}`,
            {
              domains: editRoleData.domains,
              organizationPermissions: editRoleData.organizationPermissions,
              allDomainsAccess: editRoleData.allDomainsAccess,
            },
          );

          this.$admin.toast.success(this.$t("settings.workplace_users_edit_role_success"));
          await this.refreshCurrentlyActiveOrganizationData();
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("error")));
        } finally {
          this.listLoadingLocalState = false;
          this.editRoleModalDisplayed = false;
          this.editRoleLoading = false;
          this.tableSelectedItems = [];
          this.resetEditRoleData();
        }
      },
      // Set the modal's form data
      editRoleAction(item) {
        this.editRoleData = {
          allDomainsAccess: item.allDomainsAccess,
          userOrganizationId: item.organizationId,
          name: item.fullName,
          organizationPermissions: [getMainRole(item.organizationPermissions)?.["@id"]],
          role: getMainRole(item.organizationPermissions)?.["@id"],
          domains: item.allDomainsAccess ? ["all_domains"] : item.domains.map(domain => domain["@id"]),
        };
        this.selectedRole = getRoleInfo(item.organizationPermissions[0]);
        this.editRoleModalDisplayed = true;
      },
      updateEditedRolePermissions(val) {
        this.editRoleData.organizationPermissions = [val["@id"]];
        this.selectedRole = val;
      },
      resetEditRoleData() {
        this.editRoleData = {
          userOrganizationId: "",
          name: "",
          role: "",
          domains: [],
          organizationPermissions: [],
          allDomainsAccess: false,
        };
      },
      async deleteUserAction(item) {
        if (
          await this.$admin.confirm(
            this.$t("settings.workplace_users_delete_user_confirmation"),
            this.$t("settings.workplace_users_delete_user_subtitle"),
            this.$t("overrides.confirm.delete_message"),
          )
        ) {
          await this.deleteUser(item, this.$t("settings.workplace_users_user_deleted"));
        }
      },
      async cancelInvitation(item) {
        await this.deleteUser(item, this.$t("settings.workplace_users_user_invitation_canceled"));
      },
      async deleteManyUserAction(item) {
        if (
          await this.$admin.confirm(
            this.$t("settings.delete_many_users", {
              count: this.tableSelectedItems.length,
            }),
            this.$t("settings.workplace_users_delete_user_subtitle"),
            this.$t("overrides.confirm.delete_message"),
          )
        ) {
          await this.deleteManyUsers(item);
        }
      },
      async deleteManyUsers(item) {
        try {
          await this.$admin.dataProvider.deleteMany("user_organizations", {
            ids: item.map(({ organizationId }) => organizationId),
          });
          this.$admin.toast.success(this.$t("settings.workplace_users_users_deleted"));
          await this.refreshCurrentlyActiveOrganizationData();
          this.tableSelectedItems = [];
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.listLoadingLocalState = false;
        }
      },
      /**
       * Actual implementation of user deletion. Both for canceling invitation and deleting user (when deleting user, we have to
       * display a confirmation dialog)
       */
      async deleteUser(userOrganization, successMessage) {
        this.listLoadingLocalState = true;
        try {
          await this.$admin.dataProvider.customHttpCall(
            "DELETE",
            `user_organizations/${userOrganization.organizationId}`,
          );
          this.$admin.toast.success(successMessage);
          await this.refreshCurrentlyActiveOrganizationData();
          this.tableSelectedItems = [];
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.listLoadingLocalState = false;
        }
      },

      // 'transfer ownership' btn should only be displayed as an action of an OWNER
      displayTransferOwnershipBtn(item) {
        // If the user on the list is not an owner, hide btn
        if (!this.userIsOwner(item.organizationPermissions)) return false;
        // If it is not the currently logged in user, hide btn
        if (replaceId(this.user.id, "/api/users/") !== item.id) return false;

        return true;
      },

      displayEditRoleBtn(item) {
        // If user is not registered, it means user is invited, we should display "cancel invitation" in that case.
        if (!item.confirmed) return false;
        // We can't Edit Role for an owner
        if (this.userIsOwner(item.organizationPermissions)) return false;
        // We cant edit role for ourselves
        if (replaceId(this.user.id, "/api/users/") === item.id) return false;
        // If current user is an editor, hide button
        if (this.userIsEditor(store.state.currentlyActiveOrganization.organizationPermissions)) return false;

        return true;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/typography/index.scss";
  @import "../../@core/preset/preset/functions.scss";

  .edit-role__content-wrapper {
    margin-top: 20px;
  }
  .transfer-ownership__content-wrapper {
    margin-top: 20px;
  }
  .edit-role__role-disclaimer {
    margin-top: 8px;
    @include text-caption();
    color: get-color("subtitle");
  }
  .info__badge {
    color: get-color("subtitle");
    @include text-caption();
    padding: 4px 12px;
    display: inline-block;
    border-radius: 4px;
    border: 1px solid get-color("border");
    margin-left: 8px;
  }
  .v-toolbar {
    border-top: 1px solid get-color("border") !important;
    padding: 16px;
    &::v-deep {
      .v-toolbar__content {
        padding: 0 !important;
      }
    }
  }
</style>
